import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import SettingsIcon from '@atlaskit/icon/core/settings';
import VideoPlayIcon from '@atlaskit/icon/core/video-play';
import { Box } from '@atlaskit/primitives/compiled/box';
import Inline from '@atlaskit/primitives/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { formatRelativeTimeAgo } from '@confluence/time-ago';

import { useFetchSpaceSuggestions } from './hooks/useFetchSpaceSuggestions.ts';
import {
	useRecordSuggestionDecision,
	type SuggestionDecision,
} from './hooks/useRecordSuggestionDecision.ts';
import { useRunSpaceOrganizer } from './hooks/useRunSpaceOrganizer.ts';
import { NeedsReviewSection } from './NeedsReviewSection.tsx';
import { NoSuggestionsFound } from './NoSuggestionsFound.tsx';
import {
	PageTreeForceFetchSubscriber,
	type ForceFetchPageIds,
} from './PageTreeForceFetchSubscriber.tsx';
import {
	clearSpaceSuggestionReviewPanelItem,
	SPACE_SUGGESTION_REVIEW_PANEL_ID,
} from './review-panel/SuggestionReviewPanelSidebar.tsx';
import { SettingsView } from './settings/SettingsView.tsx';
import type { SuggestionItem } from './SuggestionCard.tsx';

type SpaceSuggestionsProps = {
	spaceKey: string;
	/** Injected by `withFlags`; consumers only pass `spaceKey`. */
	flags: FlagsStateContainer;
};

type SpaceSuggestionsViewMode = 'list' | 'settings';

/**
 * Collects the action step ids for a suggestion. Falsy ids are filtered out so a
 * suggestion served without `actionStepId`s never produces an `undefined` entry
 * in the decisions payload (which the backend would reject with a 400).
 */
const getActionStepIds = (item: SuggestionItem): string[] => {
	const actionStepIds: string[] = [];
	for (const action of item.rawSuggestion.actions ?? []) {
		if (action.actionStepId) {
			actionStepIds.push(action.actionStepId);
		}
	}
	return actionStepIds;
};

/**
 * Collects the real, client-known content ids affected by the *accepted* actions of a suggestion,
 * so the space page tree can be force-refetched to reflect the applied change. Only content ids we
 * hold on the client are usable: `destinationAlias` (MOVE) and the new folder `alias`
 * (CREATE_FOLDER) are suggestion-scoped placeholders resolved server-side, so they are skipped —
 * refetching the moved content or the create-folder parent surfaces those changes via the tree's
 * ancestor/children refetch. Ids are de-duped (a suggestion can touch the same parent twice).
 */
const getForceFetchContentIds = (
	item: SuggestionItem,
	acceptedActionStepIds: string[],
): number[] => {
	const accepted = new Set(acceptedActionStepIds);
	const contentIds = new Set<number>();
	for (const action of item.rawSuggestion.actions ?? []) {
		if (!action.actionStepId || !accepted.has(action.actionStepId)) {
			continue;
		}
		switch (action.actionType) {
			case 'RENAME':
			case 'MOVE':
				if (action.parameters.contentId) {
					contentIds.add(action.parameters.contentId);
				}
				break;
			case 'CREATE_FOLDER':
				if (action.parameters.parentContentId) {
					contentIds.add(action.parameters.parentContentId);
				}
				break;
		}
	}
	return [...contentIds];
};

function SpaceSuggestionsComponent({ spaceKey, flags }: SpaceSuggestionsProps) {
	const intl = useIntl();
	const { formatMessage } = intl;
	const { createEvent } = useConfluenceAnalyticsEvents();
	const objectSidebarActions = useObjectSidebarActions();
	const objectSidebarActionsRef = useRef(objectSidebarActions);
	objectSidebarActionsRef.current = objectSidebarActions;

	const { suggestions, isLoading, error, refetch } = useFetchSpaceSuggestions(spaceKey);
	const { run, isRunning, error: runError } = useRunSpaceOrganizer(spaceKey);
	const { recordDecision } = useRecordSuggestionDecision(spaceKey);

	// Suggestions the user has approved or rejected this session, tracked by
	// suggestion id. The card is hidden immediately by filtering it out of
	// `reviewItems` (optimistic UI); a failed decision restores the id and brings
	// the card back.
	const [removedIds, setRemovedIds] = useState<Set<string>>(() => new Set());

	// The suggestion currently selected in the list. Reset on a fresh run.
	const [selectedSuggestionId, setSelectedSuggestionId] = useState<string | null>(null);

	// Guards against `setState` after unmount: the decision call fires on click
	// and may resolve after the user has navigated away.
	const mountedRef = useRef(true);

	// Callback to force-refetch the sidebar space page tree, registered by
	// `PageTreeForceFetchSubscriber` once it has subscribed to the shared
	// `PageTreeStateUpdater`. Held in a ref so an accepted decision resolving later can call it
	// without re-running `submitDecision`'s memoization.
	const forceFetchPageIdsRef = useRef<ForceFetchPageIds | null>(null);
	const handleForceFetchPageIdsChange = useCallback(
		(forceFetchPageIds: ForceFetchPageIds | null) => {
			forceFetchPageIdsRef.current = forceFetchPageIds;
		},
		[],
	);

	// Which body view is currently shown:
	// - 'list': the default Needs review / suggestions list (the previous behavior).
	// - 'settings': the Knowledge curator settings view, toggled via the Settings icon.
	const [view, setView] = useState<SpaceSuggestionsViewMode>('list');

	useEffect(() => {
		return () => {
			clearSpaceSuggestionReviewPanelItem();
			objectSidebarActionsRef.current.hideObjectSidebarByPanelId(SPACE_SUGGESTION_REVIEW_PANEL_ID);
		};
	}, [spaceKey]);

	const goToSettings = useCallback(() => {
		clearSpaceSuggestionReviewPanelItem();
		objectSidebarActions.hideObjectSidebarByPanelId(SPACE_SUGGESTION_REVIEW_PANEL_ID);
		setView('settings');
	}, [objectSidebarActions]);

	const goToList = useCallback(() => {
		setView('list');
	}, []);

	const handleLaunch = useCallback(async () => {
		try {
			await run();
			await refetch();
			// A fresh run regenerates the suggestion set, so previous session-only
			// removals no longer apply.
			setRemovedIds(new Set());
			setSelectedSuggestionId(null);
		} catch {
			// The run and fetch hooks own their error states; this prevents an unhandled rejection.
		}
	}, [refetch, run]);

	// Shared submit path for both approve and reject. Optimistically removes the
	// card, then fires the decision to the backend immediately. On success a basic
	// auto-dismiss confirmation flag is shown; on failure the card is restored and
	// an error flag is shown. The success flag is shown only after the call
	// succeeds so a failed call never shows a contradictory confirmation + error.
	const submitDecision = useCallback(
		(item: SuggestionItem, decision: SuggestionDecision, flagTitle: string) => {
			setRemovedIds((prev) => new Set(prev).add(item.id));

			void (async () => {
				try {
					await recordDecision(item.id, decision);
					if (!mountedRef.current) {
						return;
					}
					// Refresh the sidebar space page tree so accepted RENAME/MOVE/CREATE_FOLDER
					// actions are reflected. Only accepted actions change the tree, so a reject
					// (no accepted ids) refetches nothing.
					const forceFetchContentIds = getForceFetchContentIds(
						item,
						decision.acceptedActionStepIds,
					);
					if (forceFetchContentIds.length > 0) {
						forceFetchPageIdsRef.current?.(forceFetchContentIds);
					}
					void flags.showSuccessFlag({
						id: `space-suggestion-decision-${item.id}`,
						title: flagTitle,
						isAutoDismiss: true,
					});
				} catch {
					// Skip UI updates if we've already unmounted to avoid setting state on
					// an unmounted component.
					if (!mountedRef.current) {
						return;
					}
					setRemovedIds((prev) => {
						const next = new Set(prev);
						next.delete(item.id);
						return next;
					});
					void flags.showErrorFlag({
						id: `space-suggestion-decision-error-${item.id}`,
						title: formatMessage(i18n.decisionErrorTitle),
						description: formatMessage(i18n.decisionErrorDescription),
						isAutoDismiss: true,
					});
				}
			})();
		},
		[recordDecision, flags, formatMessage],
	);

	const handleApprove = useCallback(
		(item: SuggestionItem) => {
			createEvent({
				type: 'sendUIEvent',
				data: {
					source: 'spaceSuggestionsScreen',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'spaceSuggestionApprove',
				},
			}).fire();

			submitDecision(
				item,
				{ acceptedActionStepIds: getActionStepIds(item), declinedActionStepIds: [] },
				formatMessage(i18n.approvedFlagTitle),
			);
		},
		[createEvent, formatMessage, submitDecision],
	);

	const handleReject = useCallback(
		(item: SuggestionItem) => {
			createEvent({
				type: 'sendUIEvent',
				data: {
					source: 'spaceSuggestionsScreen',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'spaceSuggestionReject',
				},
			}).fire();

			submitDecision(
				item,
				{ acceptedActionStepIds: [], declinedActionStepIds: getActionStepIds(item) },
				formatMessage(i18n.rejectedFlagTitle),
			);
		},
		[createEvent, formatMessage, submitDecision],
	);

	const handlePanelApprove = useCallback(
		(item: SuggestionItem, acceptedActionStepIds: string[], declinedActionStepIds: string[]) => {
			createEvent({
				type: 'sendUIEvent',
				data: {
					source: 'spaceSuggestionsScreen',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'spaceSuggestionPanelApprove',
				},
			}).fire();

			submitDecision(
				item,
				{ acceptedActionStepIds, declinedActionStepIds },
				formatMessage(i18n.approvedFlagTitle),
			);
		},
		[createEvent, formatMessage, submitDecision],
	);

	// Mark unmounted so in-flight decision callbacks don't touch state afterwards.
	// Set `true` on mount as well so a StrictMode remount (dev mount→unmount→mount)
	// doesn't leave the ref stuck at `false` after the first cleanup runs.
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const reviewItems: SuggestionItem[] = useMemo(
		() =>
			// Single pass: skip removed (approved/rejected) suggestions while mapping.
			suggestions.reduce<SuggestionItem[]>((items, suggestion) => {
				if (!removedIds.has(suggestion.id)) {
					items.push({
						id: suggestion.id,
						summary: suggestion.title,
						spaceKey,
						rawSuggestion: suggestion,
						timestamp: formatRelativeTimeAgo(suggestion.createdAt, intl),
					});
				}
				return items;
			}, []),
		[suggestions, removedIds, intl, spaceKey],
	);

	const isSettingsEnabled = StatsigConfigurations.getBoolean(
		'confluence-space-suggestion-settings',
		false,
	);
	const isSettingsView = isSettingsEnabled && view === 'settings';

	return (
		<Box xcss={styles.page}>
			{/* Registers the page-tree force-refetch callback; renders nothing. */}
			<PageTreeForceFetchSubscriber onForceFetchPageIdsChange={handleForceFetchPageIdsChange} />
			{/* Page header: title + action buttons (Launch + Settings/Close) */}
			<Box xcss={styles.header}>
				<Inline alignBlock="center" spread="space-between" space="space.100">
					<Heading size="large">{formatMessage(i18n.pageTitle)}</Heading>
					<Inline alignBlock="center" space="space.075">
						{!isSettingsView && (
							<Button
								iconBefore={VideoPlayIcon}
								onClick={handleLaunch}
								isLoading={isRunning}
								isDisabled={isLoading}
							>
								{formatMessage(i18n.launchButton)}
							</Button>
						)}
						{isSettingsEnabled &&
							(isSettingsView ? (
								<IconButton
									icon={CrossIcon}
									label={formatMessage(i18n.backButtonLabel)}
									appearance="subtle"
									isTooltipDisabled={false}
									onClick={goToList}
									testId="space-suggestions-back-button"
								/>
							) : (
								<IconButton
									icon={SettingsIcon}
									label={formatMessage(i18n.settingsLabel)}
									appearance="subtle"
									isTooltipDisabled={false}
									onClick={goToSettings}
									testId="space-suggestions-settings-button"
								/>
							))}
					</Inline>
				</Inline>
			</Box>

			<Box xcss={styles.gridDefault}>
				{/* Main content area */}
				<Box xcss={styles.mainContent}>
					{isSettingsView ? (
						<SettingsView />
					) : (
						<>
							{runError && (
								<Box role="alert" testId="space-suggestions-run-error">
									<Text>{formatMessage(i18n.runError)}</Text>
									<VisuallyHidden testId="space-suggestions-run-error-cause">
										{runError.message}
									</VisuallyHidden>
								</Box>
							)}
							{isLoading && (
								<Box aria-live="polite" testId="space-suggestions-loading">
									<Text>{formatMessage(i18n.loading)}</Text>
								</Box>
							)}
							{!isLoading && error && (
								<Box role="alert" testId="space-suggestions-error">
									<Text>{formatMessage(i18n.error)}</Text>
									{/* Raw cause is exposed only to assistive tech / dev tools for diagnostics. */}
									<VisuallyHidden testId="space-suggestions-error-cause">
										{error.message}
									</VisuallyHidden>
								</Box>
							)}
							{!isLoading && !error && reviewItems.length === 0 && <NoSuggestionsFound />}
							{!isLoading && !error && reviewItems.length > 0 && (
								<NeedsReviewSection
									items={reviewItems}
									onApprove={handleApprove}
									onReject={handleReject}
									onPanelApprove={handlePanelApprove}
									selectedItemId={selectedSuggestionId ?? undefined}
								/>
							)}
						</>
					)}
				</Box>
			</Box>
		</Box>
	);
}

export const SpaceSuggestions: React.ComponentType<Omit<SpaceSuggestionsProps, 'flags'>> =
	withFlags(SpaceSuggestionsComponent);

const i18n = defineMessages({
	pageTitle: {
		id: 'space-suggestions.page.knowledge-curator-title',
		defaultMessage: 'Knowledge curator',
		description: 'The main heading for the Knowledge curator (Space Suggestions) page',
	},
	launchButton: {
		id: 'space-suggestions.page.launch-button',
		defaultMessage: 'Launch',
		description:
			'Label for the Launch button in the Knowledge curator page header. Runs Space Organizer for this space.',
	},
	settingsLabel: {
		id: 'space-suggestions.page.settings-button-label',
		defaultMessage: 'Go to settings',
		description:
			'Accessible label and tooltip for the settings icon button in the Knowledge curator page header',
	},
	backButtonLabel: {
		id: 'space-suggestions.page.back-button-label',
		defaultMessage: 'Back to suggestions',
		description:
			'Accessible label and tooltip for the close (X) icon button shown in the Knowledge curator page header while the settings view is open. Clicking it returns the user to the Needs review / suggestions list.',
	},
	loading: {
		id: 'space-suggestions.page.loading',
		defaultMessage: 'Loading…',
		description: 'Shown while the Space Suggestions page is fetching data',
	},
	error: {
		id: 'space-suggestions.page.error',
		defaultMessage: 'Something went wrong while loading suggestions. Please try again later.',
		description:
			'Shown when the Space Suggestions page fails to fetch data from the backend. Generic message — the raw error text is only surfaced via the element title attribute for diagnostics.',
	},
	approvedFlagTitle: {
		id: 'space-suggestions.page.approved-flag-title',
		defaultMessage: 'Suggestion approved',
		description:
			'Title of the confirmation flag shown after the user approves a suggestion from the Knowledge curator list',
	},
	decisionErrorTitle: {
		id: 'space-suggestions.page.decision-error-title',
		defaultMessage: "Couldn't save your decision",
		description:
			'Title of the error flag shown when saving an approve/reject decision to the backend fails. The suggestion is restored to the list.',
	},
	decisionErrorDescription: {
		id: 'space-suggestions.page.decision-error-description',
		defaultMessage: 'Please try again.',
		description:
			'Body of the error flag shown when saving an approve/reject decision to the backend fails.',
	},
	rejectedFlagTitle: {
		id: 'space-suggestions.page.rejected-flag-title',
		defaultMessage: 'Suggestion rejected',
		description:
			'Title of the confirmation flag shown after the user rejects (dismisses) a suggestion from the Knowledge curator list',
	},
	runError: {
		id: 'space-suggestions.page.run-error',
		defaultMessage: 'Something went wrong while running Space Organizer. Please try again later.',
		description:
			'Shown when the user tries to manually run Space Organizer and the run request fails. Generic message — the raw error text is only surfaced to assistive technology for diagnostics.',
	},
});

const styles = cssMap({
	page: {
		paddingBlockStart: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
	header: {
		marginBottom: token('space.300'),
	},
	mainContent: {
		minWidth: '0',
	},
	gridDefault: {
		display: 'grid',
		gridTemplateColumns: '1fr',
	},
});
